import type { FeatureCollection, Feature, Geometry } from 'geojson';

/**
 * Valid GeoJSON geometry types for validation.
 */
const VALID_GEOMETRY_TYPES = [
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
] as const;

type ValidGeometryType = (typeof VALID_GEOMETRY_TYPES)[number];

/**
 * Result of a single validation check.
 */
export interface ValidationIssue {
  type: 'error' | 'warning';
  featureIndex?: number;
  message: string;
  field?: string;
}

/**
 * Complete validation result for an imported FeatureCollection.
 */
export interface ImportValidationResult {
  valid: boolean;
  featureCount: number;
  nullGeometryCount: number;
  geometryTypes: string[];
  issues: ValidationIssue[];
}

/**
 * Options for controlling import validation behavior.
 */
export interface ImportValidationOptions {
  /** If true, features with null geometries are flagged as warnings instead of errors */
  allowNullGeometries?: boolean;
  /** Expected feature count from source (for count verification) */
  expectedFeatureCount?: number;
  /** Minimum coordinate precision (number of decimal places) to verify */
  minCoordinatePrecision?: number;
}

/**
 * Validates an imported GeoJSON FeatureCollection for data integrity.
 *
 * Checks:
 * - All feature geometries are valid GeoJSON geometry types
 * - All feature attributes/properties are preserved (non-undefined)
 * - Coordinate precision is maintained
 * - Feature count matches expected count (if provided)
 * - Null geometries are flagged
 *
 * @param featureCollection - The imported FeatureCollection to validate
 * @param options - Validation options
 * @returns ImportValidationResult with validation status and issues
 */
export function validateImport(
  featureCollection: FeatureCollection,
  options: ImportValidationOptions = {}
): ImportValidationResult {
  const issues: ValidationIssue[] = [];
  const geometryTypes = new Set<string>();
  let nullGeometryCount = 0;

  // Validate top-level structure
  if (!featureCollection || featureCollection.type !== 'FeatureCollection') {
    issues.push({
      type: 'error',
      message: 'Input is not a valid FeatureCollection',
    });
    return {
      valid: false,
      featureCount: 0,
      nullGeometryCount: 0,
      geometryTypes: [],
      issues,
    };
  }

  if (!Array.isArray(featureCollection.features)) {
    issues.push({
      type: 'error',
      message: 'FeatureCollection.features is not an array',
    });
    return {
      valid: false,
      featureCount: 0,
      nullGeometryCount: 0,
      geometryTypes: [],
      issues,
    };
  }

  const featureCount = featureCollection.features.length;

  // Validate expected feature count
  if (options.expectedFeatureCount !== undefined && featureCount !== options.expectedFeatureCount) {
    issues.push({
      type: 'error',
      message: `Feature count mismatch: expected ${options.expectedFeatureCount}, got ${featureCount}`,
    });
  }

  // Validate each feature
  for (let i = 0; i < featureCollection.features.length; i++) {
    const feature = featureCollection.features[i];
    validateFeature(feature, i, options, issues, geometryTypes);

    if (feature.geometry === null || feature.geometry === undefined) {
      nullGeometryCount++;
    }
  }

  const hasErrors = issues.some((issue) => issue.type === 'error');

  return {
    valid: !hasErrors,
    featureCount,
    nullGeometryCount,
    geometryTypes: Array.from(geometryTypes),
    issues,
  };
}

/**
 * Validates a single feature for geometry and attribute integrity.
 */
function validateFeature(
  feature: Feature,
  index: number,
  options: ImportValidationOptions,
  issues: ValidationIssue[],
  geometryTypes: Set<string>
): void {
  // Check feature type
  if (!feature || feature.type !== 'Feature') {
    issues.push({
      type: 'error',
      featureIndex: index,
      message: `Feature at index ${index} is not a valid Feature object`,
    });
    return;
  }

  // Validate geometry
  if (feature.geometry === null || feature.geometry === undefined) {
    const issueType = options.allowNullGeometries ? 'warning' : 'error';
    issues.push({
      type: issueType,
      featureIndex: index,
      message: `Feature at index ${index} has null geometry`,
      field: 'geometry',
    });
  } else {
    validateGeometry(feature.geometry, index, options, issues, geometryTypes);
  }

  // Validate properties
  validateProperties(feature.properties, index, issues);
}

/**
 * Validates a geometry object for type correctness and coordinate integrity.
 */
function validateGeometry(
  geometry: Geometry,
  featureIndex: number,
  options: ImportValidationOptions,
  issues: ValidationIssue[],
  geometryTypes: Set<string>
): void {
  // Check geometry type is valid
  if (!geometry.type || !VALID_GEOMETRY_TYPES.includes(geometry.type as ValidGeometryType)) {
    issues.push({
      type: 'error',
      featureIndex,
      message: `Feature at index ${featureIndex} has invalid geometry type: "${geometry.type}"`,
      field: 'geometry.type',
    });
    return;
  }

  geometryTypes.add(geometry.type);

  // Validate coordinates exist and are well-formed
  if (geometry.type === 'GeometryCollection') {
    if (!Array.isArray((geometry as GeoJSON.GeometryCollection).geometries)) {
      issues.push({
        type: 'error',
        featureIndex,
        message: `Feature at index ${featureIndex} has GeometryCollection without geometries array`,
        field: 'geometry.geometries',
      });
    } else {
      // Recursively validate sub-geometries
      for (const subGeom of (geometry as GeoJSON.GeometryCollection).geometries) {
        validateGeometry(subGeom, featureIndex, options, issues, geometryTypes);
      }
    }
  } else {
    // All other types require coordinates
    const coords = (geometry as { coordinates: unknown }).coordinates;
    if (!Array.isArray(coords)) {
      issues.push({
        type: 'error',
        featureIndex,
        message: `Feature at index ${featureIndex} has geometry without coordinates array`,
        field: 'geometry.coordinates',
      });
    } else {
      // Validate coordinate precision if specified
      if (options.minCoordinatePrecision !== undefined) {
        validateCoordinatePrecision(coords, featureIndex, options.minCoordinatePrecision, issues);
      }
    }
  }
}

/**
 * Validates that coordinates maintain minimum precision.
 * Recursively traverses nested coordinate arrays.
 */
function validateCoordinatePrecision(
  coords: unknown[],
  featureIndex: number,
  minPrecision: number,
  issues: ValidationIssue[]
): void {
  if (coords.length === 0) return;

  // Check if this is a coordinate pair [lng, lat, ...] or a nested array
  if (typeof coords[0] === 'number') {
    // This is a coordinate pair
    for (const coord of coords) {
      if (typeof coord === 'number') {
        const decimalPlaces = getDecimalPlaces(coord);
        if (decimalPlaces < minPrecision && coord !== 0 && Number.isFinite(coord)) {
          issues.push({
            type: 'warning',
            featureIndex,
            message: `Feature at index ${featureIndex} has coordinate with precision ${decimalPlaces} (minimum: ${minPrecision})`,
            field: 'geometry.coordinates',
          });
          return; // Only report once per feature
        }
      }
    }
  } else if (Array.isArray(coords[0])) {
    // Nested array - recurse
    for (const nested of coords) {
      if (Array.isArray(nested)) {
        validateCoordinatePrecision(nested, featureIndex, minPrecision, issues);
      }
    }
  }
}

/**
 * Gets the number of decimal places in a number.
 */
function getDecimalPlaces(num: number): number {
  if (!Number.isFinite(num)) return 0;
  const str = num.toString();
  const dotIndex = str.indexOf('.');
  if (dotIndex === -1) return 0;
  return str.length - dotIndex - 1;
}

/**
 * Validates feature properties for integrity.
 */
function validateProperties(
  properties: Record<string, unknown> | null | undefined,
  featureIndex: number,
  issues: ValidationIssue[]
): void {
  // Properties can be null per GeoJSON spec - that's valid
  if (properties === null || properties === undefined) {
    return;
  }

  if (typeof properties !== 'object' || Array.isArray(properties)) {
    issues.push({
      type: 'error',
      featureIndex,
      message: `Feature at index ${featureIndex} has invalid properties (must be an object or null)`,
      field: 'properties',
    });
    return;
  }

  // Check for undefined values in properties (indicates data loss)
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) {
      issues.push({
        type: 'warning',
        featureIndex,
        message: `Feature at index ${featureIndex} has undefined value for property "${key}"`,
        field: `properties.${key}`,
      });
    }
  }
}

/**
 * Filters out features with null geometries from a FeatureCollection.
 * Returns a new FeatureCollection with only features that have valid geometries.
 *
 * @param featureCollection - The input FeatureCollection
 * @returns A new FeatureCollection with null-geometry features removed
 */
export function filterNullGeometries(featureCollection: FeatureCollection): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: featureCollection.features.filter(
      (feature) => feature.geometry !== null && feature.geometry !== undefined
    ),
  };
}
